import React, { useEffect, useRef, useState } from "react";
import {
  Building2,
  Check,
  Edit,
  IdCard,
  KeyRound,
  Loader2,
  Mail,
  Phone,
  QrCode,
  ShieldCheck,
  Trash2,
  UploadCloud,
  User as UserIcon,
  Wallet,
  X,
} from "lucide-react";
import { User } from "../../types";
import { apiService } from "../../utils/apiService";
import { displayRoles } from "../../utils/accessControl";
import {
  formatPhilippinePhoneInput,
  normalizePhilippinePhone,
} from "../../utils/phoneNumber";
import { showSileoToast } from "../../utils/sileoToast";
import { mapUserDataFromBackend } from "../../utils/userDataMapper";
import { ChangePasswordModal } from "../auth/ChangePasswordModal";
import { Alert, AlertDescription } from "../ui/alert";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";

export interface AdminSettingsPageProps {
  currentUser: User;
  onUpdateCurrentUser: (user: User) => void;
}

interface ProfileFormState {
  name: string;
  email: string;
  phone: string;
  school: string;
  gcashNumber: string;
}

interface ProfileFormErrors {
  name?: string;
  email?: string;
  phone?: string;
  school?: string;
  gcashNumber?: string;
  general?: string;
}

const MAX_QR_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_QR_MIME_TYPES = new Set(["image/jpeg", "image/png"]);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toFormState(user: User): ProfileFormState {
  return {
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    school: user.school || "",
    gcashNumber: user.gcashNumber || "",
  };
}

function getInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "U"
  );
}

function validateProfileForm(formData: ProfileFormState): ProfileFormErrors {
  const errors: ProfileFormErrors = {};
  const trimmedName = formData.name.trim();
  const trimmedEmail = formData.email.trim();
  const trimmedSchool = formData.school.trim();

  if (trimmedName.length < 2) {
    errors.name = "Full name must be at least 2 characters.";
  } else if (trimmedName.length > 120) {
    errors.name = "Full name cannot exceed 120 characters.";
  }

  if (!trimmedEmail) {
    errors.email = "Email address is required.";
  } else if (trimmedEmail.length > 254 || !EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = "Enter a valid email address.";
  }

  if (trimmedSchool.length > 120) {
    errors.school = "Department / institution cannot exceed 120 characters.";
  }

  if (formData.phone.trim()) {
    try {
      normalizePhilippinePhone(formData.phone);
    } catch {
      errors.phone = "Use a valid PH mobile number (+639XXXXXXXXX or 09XXXXXXXXX).";
    }
  }

  if (formData.gcashNumber.trim()) {
    try {
      normalizePhilippinePhone(formData.gcashNumber);
    } catch {
      errors.gcashNumber = "Use a valid PH mobile number (+639XXXXXXXXX or 09XXXXXXXXX).";
    }
  }

  return errors;
}

function InfoItem({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value?: string | null;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="flex items-center gap-2 text-sm font-medium text-foreground break-all">
        {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span>{value && value.trim() !== "" ? value : "—"}</span>
      </div>
    </div>
  );
}

export function AdminSettingsPage({
  currentUser,
  onUpdateCurrentUser,
}: AdminSettingsPageProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingQr, setIsUploadingQr] = useState(false);
  const [isRemovingQr, setIsRemovingQr] = useState(false);
  const [confirmingRemoveQr, setConfirmingRemoveQr] = useState(false);
  const [qrLoadError, setQrLoadError] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [gcashQrUrl, setGcashQrUrl] = useState<string | null>(
    currentUser.gcashQrUrl || currentUser.gcash_qr_url || null,
  );
  const [formData, setFormData] = useState<ProfileFormState>(() =>
    toFormState(currentUser),
  );
  const [errors, setErrors] = useState<ProfileFormErrors>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const onUpdateRef = useRef(onUpdateCurrentUser);
  onUpdateRef.current = onUpdateCurrentUser;

  const roleLabels = displayRoles(currentUser);
  const roleSummary = roleLabels.length > 0 ? roleLabels.join(" · ") : "Team Member";
  const employeeId = currentUser.employeeNumber || "Not assigned";
  const secureId = currentUser.secureId || "N/A";

  useEffect(() => {
    let active = true;

    const fetchFreshProfile = async () => {
      try {
        const response = await apiService.getMe();
        const rawUser = response.data?.user;
        if (!active || !rawUser) return;
        const freshUser: User = {
          ...mapUserDataFromBackend(rawUser),
          id: rawUser.id ?? currentUser.id,
        };
        onUpdateRef.current(freshUser);
        setGcashQrUrl(freshUser.gcashQrUrl || null);
        setQrLoadError(false);
      } catch (err) {
        console.error("Failed to refresh user profile:", err);
      }
    };

    fetchFreshProfile();
    return () => {
      active = false;
    };
  }, [currentUser.id]);

  useEffect(() => {
    if (!isEditing) {
      setFormData({
        name: currentUser.name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        school: currentUser.school || "",
        gcashNumber: currentUser.gcashNumber || "",
      });
    }
    setGcashQrUrl(currentUser.gcashQrUrl || currentUser.gcash_qr_url || null);
  }, [
    currentUser.name,
    currentUser.email,
    currentUser.phone,
    currentUser.school,
    currentUser.gcashNumber,
    currentUser.gcashQrUrl,
    currentUser.gcash_qr_url,
    isEditing,
  ]);

  const handleStartEdit = () => {
    setFormData(toFormState(currentUser));
    setErrors({});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData(toFormState(currentUser));
    setErrors({});
    setIsEditing(false);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = validateProfileForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim().toLowerCase();
    const trimmedSchool = formData.school.trim();
    const normalizedPhone = formData.phone.trim()
      ? normalizePhilippinePhone(formData.phone) ?? null
      : null;
    const normalizedGcash = formData.gcashNumber.trim()
      ? normalizePhilippinePhone(formData.gcashNumber) ?? null
      : null;

    setIsSaving(true);
    setErrors({});

    try {
      const response = await apiService.updateUser(currentUser.id, {
        name: trimmedName,
        email: trimmedEmail,
        school: trimmedSchool,
        phone: normalizedPhone,
        gcashNumber: normalizedGcash,
      });

      if (response.error) {
        throw new Error(response.error);
      }

      const updatedUser: User = response.data?.user
        ? {
            ...mapUserDataFromBackend(response.data.user),
            id: response.data.user.id ?? currentUser.id,
          }
        : {
            ...currentUser,
            name: trimmedName,
            email: trimmedEmail,
            school: trimmedSchool,
            phone: normalizedPhone,
            gcashNumber: normalizedGcash,
          };

      onUpdateCurrentUser(updatedUser);
      setIsEditing(false);
      showSileoToast({
        type: "success",
        title: "Profile updated",
        description: "Your account information has been saved.",
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to save profile changes. Please try again.";
      setErrors({ general: message });
      showSileoToast({
        type: "error",
        title: "Update failed",
        description: message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleQrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_QR_MIME_TYPES.has(file.type)) {
      showSileoToast({
        type: "error",
        title: "Unsupported image format",
        description: "Please upload a JPEG or PNG image.",
      });
      event.target.value = "";
      return;
    }

    if (file.size <= 0 || file.size > MAX_QR_FILE_BYTES) {
      showSileoToast({
        type: "error",
        title: "Invalid file size",
        description: "QR image must be 5 MB or smaller.",
      });
      event.target.value = "";
      return;
    }

    setIsUploadingQr(true);
    try {
      const response = await apiService.uploadGcashQr(file);
      if (response.error) {
        throw new Error(response.error);
      }
      const qrUrl = response.data?.gcash_qr_url;
      if (!qrUrl) {
        throw new Error("No QR URL returned from server.");
      }

      setGcashQrUrl(qrUrl);
      setQrLoadError(false);
      setConfirmingRemoveQr(false);
      onUpdateCurrentUser({ ...currentUser, gcashQrUrl: qrUrl });
      showSileoToast({
        type: "success",
        title: "GCash QR uploaded",
        description: "Your payment QR code has been updated.",
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to upload QR code.";
      showSileoToast({
        type: "error",
        title: "Upload failed",
        description: message,
      });
    } finally {
      setIsUploadingQr(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirmRemoveQr = async () => {
    setIsRemovingQr(true);
    try {
      const response = await apiService.updateUser(currentUser.id, {
        gcash_qr_url: null,
      });
      if (response.error) {
        throw new Error(response.error);
      }
      setGcashQrUrl(null);
      setQrLoadError(false);
      setConfirmingRemoveQr(false);
      onUpdateCurrentUser({ ...currentUser, gcashQrUrl: null, gcash_qr_url: null });
      showSileoToast({
        type: "success",
        title: "GCash QR removed",
        description: "Your payment QR code has been cleared.",
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to remove QR code.";
      showSileoToast({
        type: "error",
        title: "Remove failed",
        description: message,
      });
    } finally {
      setIsRemovingQr(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your personal information, payout details, and account security.
        </p>
      </div>

      <Card className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden">
        <CardHeader className="px-6 py-6 md:px-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-border">
                <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
                  {getInitials(currentUser.name)}
                </AvatarFallback>
              </Avatar>

              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                    {currentUser.name}
                  </h2>
                  <Badge
                    variant="secondary"
                    className="bg-muted text-muted-foreground border border-border text-xs font-medium px-2.5 py-0.5 flex items-center gap-1.5"
                  >
                    <UserIcon className="h-3.5 w-3.5" />
                    {roleSummary}
                  </Badge>
                </div>
                <p className="text-xs font-medium text-muted-foreground">
                  {currentUser.email}
                </p>
              </div>
            </div>

            {!isEditing && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="gap-2"
                >
                  <KeyRound className="h-4 w-4" />
                  Change Password
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleStartEdit}
                  className="gap-2 bg-primary text-primary-foreground hover:bg-secondary"
                >
                  <Edit className="h-4 w-4" />
                  Edit Information
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="p-6 md:p-8 space-y-8">
          {/* Editable Personal & Payout Information */}
          <section aria-labelledby="personal-info-heading" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3
                  id="personal-info-heading"
                  className="text-lg font-semibold text-foreground"
                >
                  Personal & Contact Information
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Keep your contact details and GCash number up to date for project coordination.
                </p>
              </div>
            </div>

            {isEditing ? (
              <form onSubmit={handleSave} className="space-y-6" noValidate>
                {errors.general && (
                  <Alert
                    variant="destructive"
                    className="border-destructive/40 bg-destructive/10 text-destructive"
                  >
                    <AlertDescription>{errors.general}</AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="settings-full-name">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="settings-full-name"
                      maxLength={120}
                      value={formData.name}
                      onChange={(e) => {
                        setFormData((prev) => ({ ...prev, name: e.target.value }));
                        if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      placeholder="Juan Dela Cruz"
                      aria-invalid={Boolean(errors.name)}
                      className={errors.name ? "border-destructive" : ""}
                      required
                    />
                    {errors.name && (
                      <p className="text-xs text-destructive">{errors.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settings-email">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="settings-email"
                      type="email"
                      maxLength={254}
                      value={formData.email}
                      onChange={(e) => {
                        setFormData((prev) => ({ ...prev, email: e.target.value }));
                        if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      placeholder="name@rioph.com"
                      aria-invalid={Boolean(errors.email)}
                      className={errors.email ? "border-destructive" : ""}
                      required
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settings-phone">Phone Number</Label>
                    <Input
                      id="settings-phone"
                      inputMode="tel"
                      maxLength={13}
                      value={formData.phone}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const formatted =
                          raw.trim() === "" ? "" : formatPhilippinePhoneInput(raw);
                        setFormData((prev) => ({ ...prev, phone: formatted }));
                        if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
                      aria-invalid={Boolean(errors.phone)}
                      className={errors.phone ? "border-destructive" : ""}
                    />
                    {errors.phone ? (
                      <p className="text-xs text-destructive">{errors.phone}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Optional. Saved canonically as +639XXXXXXXXX.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settings-gcash">GCash Number</Label>
                    <Input
                      id="settings-gcash"
                      inputMode="tel"
                      maxLength={13}
                      value={formData.gcashNumber}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const formatted =
                          raw.trim() === "" ? "" : formatPhilippinePhoneInput(raw);
                        setFormData((prev) => ({ ...prev, gcashNumber: formatted }));
                        if (errors.gcashNumber) {
                          setErrors((prev) => ({ ...prev, gcashNumber: undefined }));
                        }
                      }}
                      placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
                      aria-invalid={Boolean(errors.gcashNumber)}
                      className={errors.gcashNumber ? "border-destructive" : ""}
                    />
                    {errors.gcashNumber ? (
                      <p className="text-xs text-destructive">{errors.gcashNumber}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Optional. Used for project payouts and transfers.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="settings-school">Department / Institution</Label>
                    <Input
                      id="settings-school"
                      maxLength={120}
                      value={formData.school}
                      onChange={(e) => {
                        setFormData((prev) => ({ ...prev, school: e.target.value }));
                        if (errors.school) setErrors((prev) => ({ ...prev, school: undefined }));
                      }}
                      placeholder="Engineering / Operations / University"
                      aria-invalid={Boolean(errors.school)}
                      className={errors.school ? "border-destructive" : ""}
                    />
                    {errors.school && (
                      <p className="text-xs text-destructive">{errors.school}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="gap-2 bg-primary text-primary-foreground hover:bg-secondary min-w-[140px]"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="grid gap-6 rounded-lg border border-border bg-muted/20 p-5 sm:grid-cols-2 lg:grid-cols-3">
                <InfoItem
                  label="Full Name"
                  value={currentUser.name}
                  icon={UserIcon}
                />
                <InfoItem
                  label="Email Address"
                  value={currentUser.email}
                  icon={Mail}
                />
                <InfoItem
                  label="Phone Number"
                  value={currentUser.phone}
                  icon={Phone}
                />
                <InfoItem
                  label="GCash Number"
                  value={currentUser.gcashNumber}
                  icon={Wallet}
                />
                <InfoItem
                  label="Department / Institution"
                  value={currentUser.school}
                  icon={Building2}
                />
              </div>
            )}
          </section>

          <Separator />

          {/* Read-Only Account Identity */}
          <section aria-labelledby="identity-heading" className="space-y-4">
            <div>
              <h3
                id="identity-heading"
                className="text-lg font-semibold text-foreground"
              >
                System Identifiers
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Assigned automatically by the system. Contact a System Administrator if role changes are needed.
              </p>
            </div>

            <div className="grid gap-6 rounded-lg border border-border bg-muted/20 p-5 sm:grid-cols-3">
              <InfoItem label="Employee ID" value={employeeId} icon={IdCard} />
              <InfoItem label="Secure ID" value={secureId} icon={ShieldCheck} />
              <InfoItem label="Access Roles" value={roleSummary} icon={UserIcon} />
            </div>
          </section>

          <Separator />

          {/* GCash QR Code Management */}
          <section aria-labelledby="gcash-qr-heading" className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3
                  id="gcash-qr-heading"
                  className="text-lg font-semibold text-foreground flex items-center gap-2"
                >
                  <QrCode className="h-5 w-5 text-foreground" />
                  GCash Payment QR Code
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Upload a JPEG or PNG image (max 5 MB) for faster project payment transfers.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  aria-label="Upload GCash QR image"
                  className="hidden"
                  onChange={handleQrUpload}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploadingQr || isRemovingQr}
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  {isUploadingQr ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-4 w-4" />
                      {gcashQrUrl ? "Replace QR" : "Upload QR"}
                    </>
                  )}
                </Button>

                {gcashQrUrl && !confirmingRemoveQr && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploadingQr || isRemovingQr}
                    onClick={() => setConfirmingRemoveQr(true)}
                    className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove QR
                  </Button>
                )}

                {gcashQrUrl && confirmingRemoveQr && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={isRemovingQr}
                      onClick={handleConfirmRemoveQr}
                    >
                      {isRemovingQr ? "Removing..." : "Confirm Remove"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={isRemovingQr}
                      onClick={() => setConfirmingRemoveQr(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {gcashQrUrl && !qrLoadError ? (
              <div className="flex justify-center sm:justify-start">
                <img
                  src={gcashQrUrl}
                  alt="GCash QR code"
                  className="h-52 w-52 rounded-md border border-border bg-background object-contain p-2"
                  loading="lazy"
                  onError={() => setQrLoadError(true)}
                />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center">
                <p className="text-sm font-medium text-foreground">
                  {qrLoadError
                    ? "Unable to display current QR image. Upload a new JPEG or PNG file to replace it."
                    : "No GCash QR code uploaded yet."}
                </p>
              </div>
            )}
          </section>

          <Separator />

          {/* Account Security */}
          <section
            aria-labelledby="security-heading"
            className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border bg-muted/20 p-5"
          >
            <div className="space-y-1">
              <h3
                id="security-heading"
                className="text-base font-semibold text-foreground flex items-center gap-2"
              >
                <KeyRound className="h-4 w-4 text-foreground" />
                Password & Security
              </h3>
              <p className="text-xs text-muted-foreground">
                Update your account password anytime. Changing your password revokes other active sessions.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPasswordModalOpen(true)}
              className="gap-2 shrink-0"
            >
              <KeyRound className="h-4 w-4" />
              Change Password
            </Button>
          </section>
        </CardContent>
      </Card>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        forced={false}
        currentUser={currentUser}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={(updatedUser) => {
          onUpdateCurrentUser(updatedUser);
          setIsPasswordModalOpen(false);
          showSileoToast({
            type: "success",
            title: "Password updated",
            description: "Your new password is now active.",
          });
        }}
      />
    </div>
  );
}
